import { replace } from 'connected-react-router';
import {
	findPasswordReset,
	PasswordResetErrorResponse,
	updatePassword as apiUpdatePassword,
} from '~/Api/Authentication/Password';
import { invokeApi, resolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage';
import isValidPassword from '~/utils/Password/isValidPassword';
import { ThunkAction } from '~/Store';

import ActionTypes from './ActionTypes';

export function fetchPasswordResetWithRedirect(
	token: string | null,
	userId: string | null,
	redirectUrl: string
): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const errors = await dispatch(fetchPasswordReset(token, userId, redirectUrl));
		if (errors.resetNotFound) {
			dispatch(replace({ pathname: '/reset-password' }));
			dispatch(flashErrorMessage('Link for password change already expired or was used, please request new one'));
		}
	};
}

export function fetchPasswordReset(
	token: string | null,
	userId: string | null,
	redirectUrl: string
): ThunkAction<
	Promise<{
		resetNotFound: boolean;
	}>,
	any
> {
	return async (dispatch) => {
		const errors = { resetNotFound: false };

		if (!token || !userId) {
			dispatch(replace({ pathname: '/' }));
			return errors;
		}

		const request = dispatch(unhandledFetchPasswordReset(token, userId));
		dispatch(replace({ pathname: redirectUrl }));

		try {
			await request;
		} catch (errorResponse) {
			if (!(errorResponse instanceof PasswordResetErrorResponse)) {
				dispatch(handleError(errorResponse));
				return errors;
			}
			if (errorResponse.handlePasswordResetNotFound()) {
				return { ...errors, resetNotFound: true };
			}
			dispatch(handleError(errorResponse));
		}

		return errors;
	};
}

export function updatePassword(
	password: string,
	passwordCheck: string
): ThunkAction<
	Promise<{
		resetNotFound: boolean;
		passwordError: string | null;
		passwordCheckError: string | null;
	}>,
	any
> {
	return async (dispatch) => {
		const errors: {
			resetNotFound: boolean;
			passwordError: null | string;
			passwordCheckError: null | string;
		} = {
			resetNotFound: false,
			passwordError: null,
			passwordCheckError: null,
		};

		if (!isValidPassword(password)) {
			errors.passwordError = 'Password should be at least 8 characters long';
		} else if (password !== passwordCheck) {
			errors.passwordCheckError = 'Password does not match. Please try again';
		}

		if (errors.passwordError !== null || errors.passwordCheckError !== null) {
			return errors;
		}

		try {
			await dispatch(unhandledUpdatePassword(password));
			dispatch(replace({ pathname: '/login' }));
		} catch (errorResponse) {
			if (!(errorResponse instanceof PasswordResetErrorResponse)) {
				dispatch(handleError(errorResponse));
				return errors;
			}
			if (errorResponse.handlePasswordResetNotFound()) {
				return { ...errors, resetNotFound: true };
			}
			dispatch(handleError(errorResponse));
		}

		return errors;
	};
}

function unhandledFetchPasswordReset(token: string, userId: string): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const request = dispatch(invokeApi(findPasswordReset, token, userId));
		dispatch({ type: ActionTypes.requestReset, request, token, userId });
		await resolve(request, (response) => {
			dispatch({
				type: ActionTypes.receiveReset,
				request,
				reset: response ? response.passwordReset : null,
			});
		});
	};
}

function unhandledUpdatePassword(password: string): ThunkAction<Promise<void>, any> {
	return async (dispatch, getState) => {
		const state = getState();
		const request = dispatch(
			invokeApi(apiUpdatePassword, state.passwordChange.token, state.passwordChange.userId, password)
		);
		dispatch({ type: ActionTypes.requestPasswordUpdate, request });
		await resolve(request, () => dispatch({ type: ActionTypes.receivePasswordUpdate, request }));
	};
}
